import CloseIcon from '@mui/icons-material/Close';
import { Button, Drawer, IconButton, Paper, Stack, Theme, Typography, useTheme } from "@mui/material";
import { SxProps } from '@mui/system';
import React, { PropsWithChildren, type JSX } from "react";
import { isMobile, isTablet } from 'react-device-detect';
import { useTranslation } from "react-i18next";

export type SideSheetFieldPropsBase = {
    id?: string;
    testId?: string;
    label?: string | JSX.Element | null;
    ariaLabel?: string;
    readOnly?: boolean;
    helperText?: string;
    sx?: SxProps<Theme>;
    disabled?: boolean;
};

export type SideSheetProps = {
    open: boolean;
    title: string;
    onClose?: () => void;
    onReset?: () => void;
    variant?: 'permanent' | 'persistent' | 'temporary';
    anchor?: 'left' | 'top' | 'right' | 'bottom';
    width?: number;
    hideCloseButton?: boolean;
    hideResetButton?: boolean;
};

export default function SideSheet({
    open,
    title,
    onClose,
    onReset,
    children,
    variant,
    anchor,
    width,
    hideCloseButton,
    hideResetButton
}: PropsWithChildren<SideSheetProps>) {
    const theme = useTheme();
    const isPhone = isMobile && !isTablet;

    return (
        <>
            {!isPhone && variant !== 'temporary' &&
                <Paper
                    variant="outlined"
                    sx={{
                        width: open
                            ? theme.spacing(42)
                            : 0,
                        minWidth: open
                            ? theme.spacing(42)
                            : 0,
                        maxWidth: open
                            ? theme.spacing(42)
                            : 0,
                        transition: theme.transitions.create([
                            "width",
                            "min-width",
                            "max-width"
                        ], {
                            easing: theme.transitions.easing.sharp,
                            duration: open
                                ? theme.transitions.duration.enteringScreen
                                : theme.transitions.duration.leavingScreen,
                        }),
                        display: "flex",
                        overflow: "hidden"
                    }}
                >
                    <SideSheetContainer
                        title={title}
                        onClose={onClose}
                        hideCloseButton={hideCloseButton}
                        hideResetButton={hideResetButton}
                        onReset={onReset}
                    >
                        {children}
                    </SideSheetContainer>
                </Paper>
            }
            {/* {isPhone &&  */}
            {(isPhone || variant === 'temporary') &&
            
                <Drawer
                    variant={"temporary"}
                    anchor={anchor ?? "right"}
                    sx={{zIndex: 1300}}
                    open={open}
                    onClose={onClose}
                    slotProps={{
                        paper: {
                            sx: {
                                overflowX: 'hidden',
                                whiteSpace: "nowrap",
                                transition: theme.transitions.create("width", {
                                    easing: theme.transitions.easing.sharp,
                                    duration: open
                                        ? theme.transitions.duration.enteringScreen
                                        : theme.transitions.duration.leavingScreen,
                                }),
                                width: anchor !== 'bottom' ? width ? width : `calc(100% - ${theme.spacing(7)})` : undefined,
                                padding: 'env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left)'
                            }
                        }
                    }}
                >
                    <SideSheetContainer
                        title={title}
                        onClose={onClose}
                        hideCloseButton={true}
                        hideResetButton={hideResetButton}
                        onReset={onReset}
                    >
                        {children}
                    </SideSheetContainer>
                </Drawer>
            }
        </>
    );
}

// SideSheetContainer
type SideSheetContainerProps = {
    title: any;
    hideCloseButton?: boolean;
    hideResetButton?: boolean;
    onClose?: () => void;
    onReset?: () => void;
    children: any;
};

function SideSheetContainer({
    title,
    hideCloseButton,
    hideResetButton,
    onClose,
    onReset,
    children
}: PropsWithChildren<SideSheetContainerProps>) {
    const { t } = useTranslation();

    return (
        <Stack
            spacing={1}
            direction="column"
            sx={{
                overflow: "hidden",
                flexGrow: 1,
                padding: 1
            }}
            onKeyDown={(event) => {
                if (event.key === 'Enter') {
                    event.preventDefault();
                }
            }}
        >
            <SideSheetHeader
                title={title}
                hideCloseButton={hideCloseButton}
                onClose={onClose}
            />

            <SideSheetContent>
                {children}
            </SideSheetContent>

            {onReset && !hideResetButton &&
            <SideSheetActions>
                <Button
                    sx={{
                        marginLeft: "auto"
                    }}
                    onClick={() => { onReset && onReset(); }}
                >
                    {t("buttons.reset")}
                </Button>
            </SideSheetActions>
            }
        </Stack>
    );
}

// SideSheetHeader
type SideSheetHeaderProps = {
    title: any;
    hideCloseButton?: boolean;
    onClose?: () => void;
};

function SideSheetHeader({
    title,
    hideCloseButton,
    onClose
}: PropsWithChildren<SideSheetHeaderProps>) {
    return (
        <Stack
            direction="row"
            sx={{
                alignItems: "center",
                marginTop: "auto",
                flexGrow: 0
            }}>
            <Typography variant="h6">{title}</Typography>
            {onClose && !hideCloseButton &&
                <IconButton
                    sx={{
                        marginLeft: "auto"
                    }}
                    onClick={() => { onClose && onClose(); }}>
                    <CloseIcon />
                </IconButton>}
        </Stack>
    );
}

// SideSheetContent
type SideSheetContentProps = {
};

function SideSheetContent({
    children
}: PropsWithChildren<SideSheetContentProps>) {
    return (
        <Stack
            direction="column"
            spacing={2}

            sx={{
                overflowY: "auto",
                flexGrow: 1,
                paddingLeft: 1,
                paddingRight: 1
            }}>
            {children}
        </Stack>
    );
}

// SideSheetActions
type SideSheetActionsProps = {
};

function SideSheetActions({
    children
}: PropsWithChildren<SideSheetActionsProps>) {
    return (
        <Stack
            direction="row"
            sx={{
                alignItems: "center",
                marginTop: "auto",
                flexGrow: 0
            }}>
            {children}
        </Stack>
    );
}